import { defineStore } from 'pinia';
import helpers from '@/helpers/helpers'
import { object, string } from 'yup';
import * as customRules from '@/validation/rules'
import validation from '@/helpers/validation'
import axios from 'axios'

export const useFormStore = (formId) =>  { return defineStore(formId, {
    state: () => (
      {
        /**
         * The Form Config object.
         * 
         * This is where all of the data about the form lives.
         * 
         * This should not be modified.
         * 
         * @param {Object}
         */
        formConfig: {},
      
        /**
         * The Submit object.
         * 
         * This is where all of the RFI submission data is stored.
         * 
         * @param {Object}
         */
        submit: {},
      
        /**
         * Identifies form submission occurring.
         * 
         * This boolean identifies that a form submission is in progress.
         * 
         * @param {Boolean}
         */
        isSubmitting: false,
      
        /**
         * The submission URL for the form.
         * 
         * @param {String}
         */
        submissionUrl: 'https://rfi.edu.help/v1/leads',
      
        /**
         * The URL used for validating email addresses
         * 
         * @param {String}
         */
        emailValidationUrl: 'https://rfi.edu.help/v1/validate?field=email',
      
        /**
         * The URL used for validating phone numbers
         * 
         * @param {String}
         */
        phoneValidationUrl: 'https://rfi.edu.help/v1/validate?field=phone',
      
        /**
         * The URL used for validating zip/postal codes
         * 
         * @param {String}
         */
        zipValidationUrl: 'https://rfi.edu.help/v1/validate?field=zip',
      
        /**
         * Computed metadata about the form config steps.
         * 
         * @param {Object}
         */
        steps: {
          /**
           * The current step the form is on.
           * 
           * @param {Number}
           */
          current: 1
        },
      
        /**
         * The partner's name.
         * 
         * @param {String}
         */
        partnerName: 'PLEASE SET THE PARTNER NAME',
      
        /**
         * The embed id.
         * 
         * @param {String}
         */
         embedId: '',
      
        /**
         * The data to use when filter programs.
         * 
         * @param {Object}
         */
        programsFilter: {},
      
        /**
         * The fields that should not be rendered.
         * 
         * @param {Array}
         */
        hiddenFields: [],
      
        /**
         * The page context for which form variant should be loaded.
         *
         * @param {String}
         */
        pageContext: '',
      
        /**
         * The form variant object.
         *
         * @param {Object}
         */
         formVariant: {},
         
         /**
          * The last phone entry that passed validation.
          * 
          * @param {String}
          */
         lastPhone: '',
      
         /**
          * The last email entry that passed validation.
          * 
          * @param {String}
          */
         lastEmail: '',
      
         /**
          * The last zipcode entry that passed validation.
          * 
          * @param {String}
          */
         lastZip: '',
      
         /**
         * The schemaObject used for validations
         * 
         * @param {Object}
         */
         schemaObject: {schema: {}, lut: {}, errors: {}},
      
        /**
         * The schemaObject used for validations
         * 
         * @param {Object}
         */
         schemaOverrides: { fields: {} },
      
        /**
         * The programs that are available on the form.
         * 
         * @param {Array}
         */
        availablePrograms: [],
      
        /**
         * The current thank you page.
         * 
         * @param {Array}
         */
        thankYouPage: '',
      
        /**
         * The object to store all redirects (default + additional).
         * 
         * @param {String}
         */
        additionalRedirects: {},  
        
         /**
          * The full phone entry with dial code.
          * 
          * @param {String}
          */
         fullPhone: '',

      
      }
    ),
    getters: {
      /**
         * The id of the current active instance of the app.
         * 
         * @param {Object} state
         * @param {String} payload
         * 
         * @returns {Void}
         */  
        getAppId: (state) => {
          return state.appId 
        },  
        
        /**
         * The partner's name.
         * 
         * @param {Object} state
         * 
         * @returns {String}
         */
        getPartnerName: (state) => {
          return state.partnerName
        },
      
        /**
         * The form config stored in the state.
         * 
         * @param {Object} state 
         * 
         * @returns {Object}
         */
        getFormConfig: (state) => {
          return state.formConfig
        },
      
        /**
         * The page context.
         * 
         * @param {Object} state 
         * 
         * @returns {Object}
         */
        getPageContext: (state) => {
          return state.pageContext
        },
        /**
         * The form variant from the form config in the state.
         *          * 
         * @returns {Object}
         */
         getFormVariant: (state) =>  state.formVariant,        
      
        /**
         * The steps present on the form config.
         * 
         * @param {Object} state 
         * 
         * @returns {Array}
         */
        getFormSteps() {
          let formVariant = this.getFormVariant
      
          if (! Object.prototype.hasOwnProperty.call(formVariant, 'steps')) {
            return []
          }
      
          return formVariant.steps
        },
      
        /**
         * The number of steps associated with the form config.
         * 
         * @param {Object} state 
         * 
         * @returns {Number}
         */
        getFormStepsCount() {
          let formVariant = this.getFormVariant
      
          if (! Object.prototype.hasOwnProperty.call(formVariant, 'steps')) {
            return 0  
          }
      
          return formVariant.steps.length
        },
      
        /**
         * The current step of the form.
         * 
         * @param {Object} state
         * 
         * @returns {Number}
         */
        getCurrentFormStep: (state) => {
          return state.steps.current
        },
      
        /**
         * The list of form fields associated with the form config.
         * 
         * Note: This will return the full list of fields across all steps.
         * 
         * @param {Object} state 
         * 
         * @returns {Array}
         */
        getFormFields() {
          let formVariant = this.getFormVariant
      
          if (! Object.prototype.hasOwnProperty.call(formVariant, 'steps')) {
            return []
          }
      
          let fields = []
      
          formVariant.steps.forEach(stepFields => {
            fields.push(...stepFields)
          })
      
          return fields
        },
      
        /**
         * The programs associated with the form config.
         * 
         * @param {Object} state 
         * 
         * @returns {Array}
         */
        getPrograms() {
          let formConfig = this.getFormConfig
      
          if (! Object.prototype.hasOwnProperty.call(formConfig, 'programs')) {
            return []
          }
      
          return formConfig.programs
        },
      
        /** 
         * The submit object to be sent with the RFI.
         * 
         * @param {Object} state
         * 
         * @returns {Object}
         */
        getSubmitObject: (state) => {
          return state.submit
        },
      
        /**
         * The submission URL for the form.
         * 
         * @param {Object} state
         * 
         * @returns {String}
         */
        getSubmissionUrl: (state) => {
          return state.submissionUrl
        },
      
        /**
         * The redirect URL for the form.
         * 
         * @param {Object} state
         * 
         * @returns {String}
         */
        getRedirectUrl() {
          let formVariant = this.getFormVariant
      
          if (! Object.prototype.hasOwnProperty.call(formVariant, 'redirect')) {
            return ''
          }
      
          return formVariant.redirect
        },
      
        /** 
         * The currently selected country.
         * 
         * @param {Object} state
         * 
         * @returns {String}
         */
        getSelectedCountry() {
          let submitObject = this.getSubmitObject
      
          if (! Object.prototype.hasOwnProperty.call(submitObject, 'country')) {
            return ''
          }
      
          return submitObject.country
        },
      
        /**
         * The currently selected program ID.
         * 
         * @param {Object} state
         * 
         * @returns {String}
         */
        getSelectedProgramId() {
          let submitObject = this.getSubmitObject
      
          if (! Object.prototype.hasOwnProperty.call(submitObject, 'program')) {
            return ''
          }
      
          return submitObject.program
        },
      
        /**
         * The currently selected program.
         * 
         * @param {Object} state
         * 
         * @returns {Object}
         */
        getSelectedProgram() {
          let programId = this.getSelectedProgramId
          let programs = this.getPrograms
      
          let program = programs.filter(program => program.id === programId)[0] ?? {}
      
          return program
        },
      
        /**
         * The selected program's qualifier.
         * 
         * @param {Object} state
         * 
         * @returns {Object}
         */
        getSelectedProgramQualifier() {
          let program = this.getSelectedProgram
          let qualifier
      
          try {
            qualifier = program.programQualifier.data
          } catch {
            qualifier = {}
          }
      
          return qualifier
        },
      
        /** 
         * The currently selected program filter.
         * 
         * @param {Object} state
         * 
         * @returns {Object}
         */
        getProgramsFilter: (state) => {
          return state.programsFilter
        },
      
        /** 
         * The fields that should not be rendered.
         * 
         * @param {Object} state
         * 
         * @returns {Array}
         */
        getHiddenFields: (state) => {
          return state.hiddenFields
        },
      
        /**
         * Get the variant types that should be retrieved by the page context, setting the string
         * as the page context
         * 
         * @returns {Object}
         */
        getVariantTypes() {
          return {default: 'thedefault', affiliate:'affiliate', abtest:'abtest'}
        },
      
        /**
         * Sets the last phone entry that passed validation.
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getLastPhone: (state) => {
          return state.lastPhone
        },
      
        /**
         * Sets the last email entry that passed validation.
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getLastEmail: (state) => {
          return state.lastEmail
        },
      
        /**
         * Sets the last zipcode entry that passed validation.
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getLastZip: (state) => {
          return state.lastZip
        },      
        /** 
         * Get the state of whether form is being submitted.
         * 
         * @param {Object} state
         * 
         * @returns {Array}
         */
        getIsSubmitting: (state) => state.isSubmitting,
      
        /**
         * Gets the yup validation schema
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getSchemaObject: (state) => state.schemaObject,      
      
        /**
       * Gets yup validation schema overrides
       * 
       * @param {Object} state
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
        getSchemaOverrides: (state) => state.schemaOverrides,
        
        /** 
         * The available programs on the form.
         * 
         * @param {Object} state
         * 
         * @returns {Array}
         */
        getAvailablePrograms: (state) => state.availablePrograms,

        /** 
         * The current thank you page.
         * 
         * @param {Object} state
         * 
         * @returns {Array}
         */
        getThankYouPage: (state) => state.thankYouPage,
        
        /**
         * Gets the object to store all redirects (default + additional).
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getAdditionalRedirects: (state) => {
          return state.additionalRedirects
        },  

        /**
         * Sets the full phone entry with dial code.
         * 
         * @param {Object} state
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        getFullPhone: (state) => {
          return state.fullPhone
        },

      },
    actions: {
      /**
       * Initializes the Freya Form Renderer plugin. 
       * 
       * @param {Object} payload
       * 
       * @returns {Void}
       */
      initializePlugin (payload) {
        if (! ('formConfig' in payload)) {
          console.error('Freya Form Renderer: A form configuration was not found. Please specify a form configuration to properly initialize the plugin.')
        } else {
          this.setPageContext(payload.pageContext ?? 'default')
          this.setFormConfig(payload.formConfig)
          
          if ('groups' in payload && payload.groups) {
            this.setProgramGroups(payload.groups)
          }
          
          this.computeAvailablePrograms({groups: payload.groups})
    
         this.updateSubmitObject({
            key: 'formId',
            value: payload.formConfig.id
          })
    
        }
        if ('appId' in payload) {
          this.setAppId(payload.appId)
        }
    
        /**
         * Set the partner name, if applicable.
         */
        if ('partnerName' in payload) {
          this.setPartnerName(payload.partnerName)
        }
    
        /**
         * Set the embed id, if applicable.
         */
        if ('embedId' in payload) {
          this.setEmbedId(payload.embedId)
        }
    
        /**
         * Set the account that the form is associated with, if applicable.
         */
        if ('accountId' in payload) {
          this.updateSubmitObject({
            key: 'accountId',
            value: payload.accountId
          })
        }
    
        /**
         * Set the variant that the renderer is to display, if applicable. If not, set to default
         */
        if ('pageContext' in payload) {
          this.updateSubmitObject({
            key: 'pageContext',
            value: payload.pageContext
          })
        } else if (!('pageContext' in payload)) {
          this.updateSubmitObject({
            key: 'pageContext',
            value: 'default'
          })
        }
    
        /**
         * Set whether or not the page is a landing page.
         */
        this.updateSubmitObject({
          key: 'isLandingPage',
          value: window.isLandingPage ?? payload.isLandingPage ?? false
        })
    
        /**
         * Set whether or not the page is an affiliate page, if applicable.
         */
        if ('isAffiliate' in payload) {
          this.updateSubmitObject({
            key: 'affiliate',
            value: payload.isAffiliate ?? false
          })
        }
    
        /**
         * Specify whether or not the form should be showcased as a single program form.
         * 
         * In order to do this, we need to make sure that we have a default program id
         * alongside the single program toggle. In the event we do not have a default
         * program id, we do NOT want to hide the programs field. In this scenario we
         * would want to showcase the programs field as a fallback for stability. 
         */
        if ('isSingleProgram' in payload && 'defaultProgramId' in payload) {
          if (payload.isSingleProgram && payload.defaultProgramId) {
            this.setHiddenFields([
              'fr-programs'
            ])
          }
        }
    
        /**
         * Set the default program id on the submit object
         */
        if ('defaultProgramId' in payload && payload.defaultProgramId) {
          /** 
           * check and see if the default program is in the programs list first 
           */
          let programsList = !!this.getAvailablePrograms ? this.getAvailablePrograms : [] //if it exists, get the programs - else set to empty

          if(!!programsList.find(program => program.id == payload.defaultProgramId)) { //if the find returns a truthy value
            this.updateSubmitObject({
              key: 'program',
              value: payload.defaultProgramId
            })
          }
    
          let program = this.getSelectedProgram
    
          if ('isAffiliate' in payload && payload.isAffiliate) {
            this.updateSubmitObject({
              key: 'leadBuyerID',
              value: program.account.data.lead_buyer_id ?? null
            })
          }
        }
         
        let program = this.getSelectedProgram
    
        this.updateSubmitObject({
          key: 'partnerCode',
          value: program?.account?.data?.internal_partner_code ?? null
        })
    
        this.updateSubmitObject({
          key: 'postingRoute',
          value: program?.account?.data?.posting_route ?? null
        })
    
        /**
         * Sets the submission URL for the form, if applicable.
         */
        if ('submissionUrl' in payload) {
          this.setSubmissionUrl(payload.submissionUrl)
        }
    
        /**
         * Set the form source.
         */
        this.updateSubmitObject({
          key: 'formSource',
          value: window.location.href
        })
    
        /**
         * Set the order ID.
         */
        this.updateSubmitObject({
          key: 'orderId',
          value: helpers.generateOrderId()
        })
    
        /**
         * Set the partner website external ID (pweid).
         */
        this.updateSubmitObject( {
          key: 'pweid',
          value: helpers.generatePartnerWebsiteExternalId()
        })
    
        if('hidden_fields' in payload.formConfig) {
          payload.formConfig['hidden_fields'].forEach(field => {
            if (field.value !== null && field.value !== undefined && field.value !== '') {
              this.updateSubmitObject({key: field.name, value: field.value});
            }
          });
        }
          
        if (typeof window.hidden_field_overrides !== 'undefined' && window.hidden_field_overrides !== null && window.hidden_field_overrides !== '') {
          window.hidden_field_overrides.forEach(field => {
            if (field.value !== null && field.value !== undefined && field.value !== '') {
              this.updateSubmitObject({key: field.name, value: field.value});
            }
          });
        }
        
        this.updateSubmitObject({
          key: 'isOrganicArticle',
          value: window.isOrganicArticle ?? payload.isOrganicArticle ?? false 
        })        
      },
        /**
       * Sets the analytics values for tracking purposes.
       * 
       * @param {Object} context 
       * @param {Object} context.commit
       * 
       * @returns {Void}
       */
      setAnalytics() {
        if (window.dataLayer) {
    
          this.updateSubmitObject({
            key: 'gaUaId',
            value: helpers.getMeasurementId()
          })
    
          this.updateSubmitObject({
            key: 'gaClientId',
            value: helpers.getClientId()
          })
        }
        
        if (window.clientIdIndex) {
            this.updateSubmitObject({
            key: 'clientIdIndex',
            value: window.clientIdIndex
          })
        }
      },
    
      /**
       * Sets the initial values for the form fields.
       * 
       * @returns {Void}
       */
      setDefaultFieldValues() {
        let formSteps = this.getFormSteps
        //check if sms consent checkbox exists
        //if it doesn't, set okToText to true
        formSteps.forEach(step => {
          let hasSms = step.find(field => field.id === 'sms-consent-checkbox')
          if (typeof hasSms == 'undefined') {
            this.updateSubmitObject({
              key: 'okToText',
              value: true
            })
          }
          step.forEach(fieldObject => {
            if ('submitKey' in fieldObject.meta && fieldObject.meta.submitKey) {
                this.updateSubmitObject({
                key: fieldObject.meta.submitKey,
                value: fieldObject.meta.default ?? ''
              })
            }
          })
        })
      },
    
      /**
       * Initialize the form variant to be used on the form.
       * 
       * 
       * @returns {Void}
       */
    
      setInitialFormVariant(payload) {
        let pageContext = this.getPageContext
        let variantTypes = this.getVariantTypes
        
        this.setFormVariant(payload.find(v => v.variantType == variantTypes[pageContext]))
      },
    
      /**
       * Sets the id of the current active instance of the app.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */  
      setAppId(payload) {
        this.appId = payload
      },
    
      /**
       * Sets the partner's name.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setPartnerName(payload) {
        this.partnerName = payload
      },
    
       /**
       * Sets the embed id.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
       setEmbedId(payload) {
        this.embedId = payload
      },
    
      /**
       * Sets the page context to the given value.
       * 
       * @param {Object} payload 
       * 
       * @returns {Void}
       */
      setPageContext(payload) {
        this.pageContext = payload
      },
    
    
      /**
       * Sets the program groups on the field.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
       setProgramGroups (payload) {
        let formSteps = this.getFormSteps
    
        formSteps.forEach(step => {
          step.forEach(fieldObject => {
            if (fieldObject.id == 'programs' && payload) {
              fieldObject.meta.groups = payload
            }
          })
        })
      },    
        
      /**
       * Sets the form config to the given value.
       * 
       * @param {Object} payload
       * 
       * @returns {Void}
       */
      setFormConfig(payload) {
        this.formConfig = payload
        
        this.setInitialFormVariant(payload.variants)
        this.setDefaultFieldValues()
        
      },
     
      /**
       * Sets the current step of the form.
       * 
       * @param {Object} payload 
       * 
       * @returns {Void}
       */
      setCurrentFormStep(payload) {
        this.steps.current = payload
      },
    
      /**
       * Increments the current form step.
       *  
       * @returns {Void}
       */
      incrementFormStep() {
        this.setCurrentFormStep(this.getCurrentFormStep + 1)
      },
    
      /**
       * Decrements the current form step.
       * 
       * @returns {Void}
       */
      decrementFormStep() {
        this.setCurrentFormStep(this.getCurrentFormStep - 1)
      },
    
      /**
       * Updates the submit object.
       * 
       * @param {Object} payload
       * 
       * @returns {Void}
       */
      updateSubmitObject(payload) {
        // this supposedly should be good enough to track reactivity in Vue 3
        this.submit[payload.key] = payload.value
      },
    
      /**
       * Sets the submission URL for the form.
       * 
       * @param {Object} context
       * @param {Object} context.commit
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setSubmissionUrl(payload) {
        this.submissionUrl = payload
      },

      /**
       * Sets the isSubmitting state.
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */

      setSubmittingState(payload) {
        this.isSubmitting = payload;
      },      
    
      /**
       * Sets the email validation URL for the form.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setEmailValidationUrl(payload) {
        this.emailValidationUrl = payload
      },
    
      /**
     * Sets the phone validation URL for the form.
     * 
     * @param {String} payload
     * 
     * @returns {Void}
     */
      setPhoneValidationUrl(payload) {
        this.phoneValidationUrl = payload
      },
    
      /**
       * Sets the zip validation URL for the form.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setZipValidationUrl(payload) {
        this.zipValidationUrl = payload
      },
    
      /**
       * Sets the programs filter.
       * 
       * @param {Object} payload 
       * 
       * @returns {Void}
       */
      setProgramsFilter(payload) {
        this.programsFilter = payload
      },
    
      /**
       * Sets the fields that should not be rendered.
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setHiddenFields(payload) {
        this.hiddenFields = payload
      },
    
      /**
       * Sets the last phone entry that passed validation.
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setLastPhone(payload) {
        this.lastPhone = payload
      },
    
      /**
       * Sets the last email entry that passed validation.
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setLastEmail(payload) {
        this.lastEmail = payload
      },
    
    
      /**
       * Sets the last zipcode entry that passed validation.
       * 
       * @param {Object} state
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setLastZip(payload) {
        this.lastZip = payload
      },  
    
      /**
       * Sets the isSubmitting state.
       * 
       * @param {Object} state
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
    
      /**
       * Submits the form to the submission URL specified.
       * 
       * 
       * @returns {Void}
       */
      submitForm() {
        this.setSubmittingState(true);
    
        let queryParameters = ''
        let referrer = ''
        let qry_string = ''
    
        this.setAnalytics()
     
        if(helpers.getCookie('rfi_query_string')) {
          queryParameters = helpers.parseQueryParameters(helpers.getCookie('rfi_query_string'))
        }
        else {
          let rfi_params = window.location.search.substr(1).replace(/&&/igm, '&'),
            pantheon_stripped = rfi_params.toUpperCase().indexOf("PANTHEON_STRIPPED")
     
          //Return null if contains pantheon stripped
          if (pantheon_stripped < 0) {
              queryParameters = helpers.parseQueryParameters(rfi_params)
          }
        }   
    
        for (let key in queryParameters) {
           this.updateSubmitObject({
             key: key,
             value: queryParameters[key]
           })
         }
    
        if (window.formAnalytics) {
          referrer = window.formAnalytics.referrer
          qry_string = window.formAnalytics.qry_string
        } else {
          referrer = document.referrer
          qry_string = window.location.search
        }
    
        this.updateSubmitObject({
          key: 'referrer',
          value: referrer
        }),
    
        this.updateSubmitObject({
          key: 'qryString',
          value: qry_string
        })
    
        this.updateSubmitObject({
          key: 'accountId',
          value: this.getSelectedProgram.account_id
        })
        
        this.updateSubmitObject({
          key: 'entryId',
          value: window.entryId
        })
    
        let submissionData = {
          ...this.getSubmitObject,
          programCode: this.getSelectedProgram.program_code ?? null,
          phone: this.getFullPhone
        }
    
        // Check the program qualifier to see if we need a substitution.
        // We only want to substitute the program if the qualifier 'fails'.
        let qualifier = this.getSelectedProgramQualifier
        let redirectUrl = this.getRedirectUrl
    
        if (
          'substitution' in qualifier && 
          qualifier.substitution && 
          submissionData.programQualifier === false
        ) {
          submissionData.program = qualifier.program_uuid
          redirectUrl = qualifier.redirect_url
        }
    
        let meta_cookie = helpers.readCookie('_fbc') ?? ''
        submissionData.fbcid = meta_cookie
                
        axios.post(
          this.getSubmissionUrl, 
          submissionData
        ).then(() => {
          this.setSubmittingState(false)
          helpers.redirectAfterSubmission(
            redirectUrl, 
            submissionData
          )
        }).catch(err => {
          this.setSubmittingState(false)
          console.error(err)
        })
      },
    
      /**
       * Sets the yup validation schema
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
    
      setSchemaObject(payload) {
        this.schemaObject = payload;
      },
    
      /**
       * Sets a field on the yup validation schema object
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
    
      setSchemaObjectValue(payload) {
        this.schemaObject[payload.key] = payload.value;
      },
    
      /**
       * Sets yup validation schema overrides
       * 
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setSchemaOverrides(payload) {
        this.schemaOverrides = payload;
      },
    
    
      /**
       * Sets available programs on the form
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      computeAvailablePrograms(payload) {

        let programs = []
        let rawPrograms = this.getPrograms
        let groups = payload.groups ? payload.groups : []
    
          /** 
           * This will compile an array of programs grouped by the specified criteria.
           * 
           * If there is no criteria to group by, we simply return a flat array of programs to render.
           */
          if (groups.length > 0) {
            groups.forEach(group => {
    
              /**
               * Program groups need to be checked explicitly since they are nested into a relation object.
               * 
               */
              if (group.field === 'group') {
    
                programs.push(
                  ...rawPrograms.filter(program => {
                  
                    return program.groups.data.find(item => item.id === group.value)
                  })
                )
    
              } else {
    
                programs.push(
                  ...rawPrograms.filter(program => {
                    return program[group.field].toLowerCase().includes(helpers.normalizeFilterValue(group.value.toLowerCase()))
                  })
                )
    
              }
    
            })
    
          } else {
            programs.push(
              ...rawPrograms
            )
    
          }
        
        this.setAvailablePrograms([...programs])
      },
    
    
      /**
       * Sets the dial code.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setAvailablePrograms(payload) {
        this.availablePrograms = payload
      },    
        
      /**
       * Sets the dial code.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setAdditionalRedirects(payload) {
        this.additionalRedirects = payload
      },
    
      /**
       * Sets the dial code.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setThankYouPage(payload) {
        this.thankYouPage = payload
      },
         
      /**
       * Sets the fields that should not be rendered.
       * 
       * @param {Object} state
       * @param {Array}  payload
       * 
       * @returns {Void}
       */
      setFormVariant(payload) {
        this.formVariant = payload
      },

      /**
       * Sets the form program.
       * 
       * @param {String} payload
       * 
       * @returns {Void}
       */
      setFormProgram(payload) {
        let programsList = !!this.getAvailablePrograms ? this.getAvailablePrograms : []
        // set the program from custom event if the program exists
        // else set the default program
        if(!!programsList.find(program => program.program_code == payload)) { //if the find returns a truthy value
          let program = programsList.find(program => program.program_code == payload)
          this.updateSubmitObject({
            key: 'program',
            value: program.id
          })
        } else if(!!programsList.find(program => program.id == payload.defaultProgramId)) { 
          this.updateSubmitObject({
            key: 'program',
            value: payload.defaultProgramId
          })
        }
      }, 

      /**
         * Sets the full phone entry with dialcode.
         * 
         * @param {Array}  payload
         * 
         * @returns {Void}
         */
        setFullPhone(payload) {
          this.fullPhone = payload
        },

      /**
       * Use form steps to generate individual schemas
       * 
       * @param {Array} steps
       * 
       * @returns {Array}
       */
      generateStepsSchema(steps){
        var schemaArray = []
        var labelLut = {}

        steps.forEach((step, idx) => {
          let parentObjectParams = {}

          step.forEach((field) => {
            if(field.component === 'fr-programs-filter'){
              labelLut[field.component] = 'programsFilter'
            } else {
              labelLut[field.component] = field.meta.submitKey
            }
            
            let so = string()

            if(!!field.meta.ignoreValidation) { return } //if the validation should be ignored/field is hidden, skip it
            
            if (field.meta.rules.length > 0) {
              field.meta.rules.forEach((rule) => {
                switch(rule.name) {
                  case 'min':
                    length = rule.parameters.hasOwnProperty('length') ? rule.parameters.length : 2
                    so = so.min(length, rule.message)
                    break
                  case 'max':
                    length = rule.parameters.hasOwnProperty('length') ? rule.parameters.length : 40
                    so = so.max(length, rule.message)
                    break
                  case 'required':
                    so = so.when('$skipRequired', ([skipRequired], schema) => {
                      if (
                        !!this.schemaOverrides.fields[field.component] 
                        && !this.schemaOverrides.fields[field.component].required
                      ){
                        skipRequired = true
                      }

                      // aveline please forgive us
                      if(
                        field.component.toLowerCase().includes("qualifier")
                        && field.meta.type !== 'education level' ){
                        return !!skipRequired ? schema : schema.oneOf([true, "true"], rule.message)
                      } else {
                        return !!skipRequired ? schema : schema.required(rule.message)
                      }
                    })

                    // this is checking if any value has been selected
                    if(field.meta.type == 'education level'){
                      so = so.test({
                        name: 'program-qualifier-dropdown',
                        test: async (value, { createError }) => {
                          if (value != "false") {
                            return true
                          } else {
                            return createError({ message: rule.message});
                          }
                        }
                      })
                    }

                    break
                  case 'email':
                    so = so.test({
                      name: 'email',
                      test: async (value, { createError }) => {
                        if (!await customRules.email(value, this.emailValidationUrl, this.getSubmitObject)) {
                          return createError({ message: validation.getValidationMessage('email')})
                        }
                        else return true;
                      }
                    })
                    break
                  case 'phone':
                    so = so.test('phone', 'Must be a valid phone number', (value) => {
                      return customRules.phone(value, this.phoneValidationUrl, this.getSubmitObject)
                    })
                    break
                  case 'postal_code':
                    so = so.test('postal_code', 'Must be a valid postal code', (value) => {
                      return customRules.postal_code(value, this.zipValidationUrl, this.getSubmitObject)
                    })
                    break
                    case 'location':
                      so = so.test('location', 'Select from autocomplete dropdown', (value) => {
                        return customRules.location(value)
                      })
                      break
                    case 'education_level':
                      so = so.test({
                        name: 'education_level', 
                        test: ( value, { createError}) => {
                          if(value != "false" && !customRules.education_level(value, this.getFormConfig, this.getSubmitObject)) {
                            return createError({ message: validation.getValidationMessage('program-qualifier-dropdown') })
                          } else return true
                        }
                      })
                      break                
                  default:
                }
              })
              if(field.component === 'fr-programs-filter'){
                parentObjectParams['programsFilter'] = so 
              } else {
                parentObjectParams[field.meta.submitKey] = so 
              }
              
            }        
          })

          if (!schemaArray[idx]) { schemaArray[idx] = [] }
          schemaArray[idx] = object(parentObjectParams)
        })

        return { schema: schemaArray, labelLut: labelLut }
      },

      /**
       * Logic to load field config and set ignore validation
       * 
       * @param {String} fieldId
       * @param {Boolean} visible
       * 
       * @returns {Array}
       */
      setIgnoreValidation(fieldId, visible) {

        let formVariant = this.getFormVariant
        let currentStep = this.getCurrentFormStep
        let steps = []

        if (! Object.prototype.hasOwnProperty.call(formVariant, 'steps')) {
          steps = []
        }

        
        steps = formVariant.steps

        if(formVariant.stepFlow == 'automatic') {
          //current step is first step only, second step is all fields
          steps = [formVariant.steps[0], formVariant.steps.flat()]
        }

        
        let fieldObj = steps[currentStep - 1].find(field => field.id == fieldId)
        
        if (!!fieldObj) { 
          fieldObj.meta.ignoreValidation = visible
        }
        

        return steps 
      },

      /** 
       * If the field is valid or not.
       * 
       * @returns {Object}
       */
      fieldHasErrors(fieldname) {
        let errors = this.schemaObject.errors[this.schemaObject.lut[fieldname]]
        return !!errors && errors.length > 0
      },

      /** 
       * If the field has been validated or not.
       * 
       * @returns {Object}
       */
      fieldHasBeenValidated(fieldname) {
        let errors = this.schemaObject.errors[this.schemaObject.lut[fieldname]]
        return !!errors
      },


      /** 
       * The CSS classes to be applied for validation purposes.
       * 
       * @returns {Object}
       */
      createValidationClasses(fieldname) {
        let errors = this.schemaObject.errors[this.schemaObject.lut[fieldname]]
        // if errors object does not exist validation has never been run
        // if errors is greater than zero then the field is invalid and vice versa
        return {
          'validated': !!errors,
          'valid': !!errors && errors.length <= 0,
          'invalid': !!errors && errors.length > 0
        }
      },      
    },

  });
};